import { AdminMembersReportPageParams } from "@/admin/insights/member-reports/AdminMembersReportPage"
import AdminMembersReportCustomProfileFieldsTableRow, {
  AdminMembersReportCustomProfileFieldsTableRowSkeleton,
} from "@/admin/insights/member-reports/tabs/overview/AdminMembersReportCustomProfileFieldsTableRow"
import { AdminMembersReportCustomProfileFieldsTablePaginationQuery } from "@/admin/insights/member-reports/tabs/overview/__generated__/AdminMembersReportCustomProfileFieldsTablePaginationQuery.graphql"
import { AdminMembersReportCustomProfileFieldsTableQuery } from "@/admin/insights/member-reports/tabs/overview/__generated__/AdminMembersReportCustomProfileFieldsTableQuery.graphql"
import { AdminMembersReportCustomProfileFieldsTable_PaginationFragment$key } from "@/admin/insights/member-reports/tabs/overview/__generated__/AdminMembersReportCustomProfileFieldsTable_PaginationFragment.graphql"
import { useActiveOrganization } from "@/core/context/ActiveOrganizationContext"
import Relay from "@/relay/relayUtils"
import { DiscoTable, DiscoTableSkeletonWithHeader, DiscoText } from "@disco-ui"
import { useQueryParamState } from "@disco-ui/tabs/DiscoQueryParamTabs"
import { useState } from "react"
import { graphql, useLazyLoadQuery, usePaginationFragment } from "react-relay"

const PROFILE_FIELDS_PER_PAGE = 10
const headers = [
  { value: "Field Name" },
  { value: "Type" },
  { value: "Question" },
  { value: "Response" },
]

function AdminMembersReportCustomProfileFieldsTable(): JSX.Element | null {
  const [queryParams] = useQueryParamState<AdminMembersReportPageParams>()
  const activeOrganization = useActiveOrganization()!
  const [activePage, setActivePage] = useState(1)

  const { node } = useLazyLoadQuery<AdminMembersReportCustomProfileFieldsTableQuery>(
    graphql`
      query AdminMembersReportCustomProfileFieldsTableQuery(
        $id: ID!
        $first: Int
        $after: String
        $last: Int
        $before: String
        $organizationMembershipId: ID!
        $isPrivate: Boolean
      ) {
        node(id: $id) {
          ... on Organization {
            id
            ...AdminMembersReportCustomProfileFieldsTable_PaginationFragment
              @arguments(
                first: $first
                after: $after
                last: $last
                before: $before
                organizationMembershipId: $organizationMembershipId
                isPrivate: $isPrivate
              )
          }
        }
      }
    `,
    {
      id: activeOrganization?.id || "",
      first: PROFILE_FIELDS_PER_PAGE,
      organizationMembershipId: queryParams.om!,
      isPrivate: activeOrganization.viewerIsOwnerOrAdmin ? undefined : false,
    },
    { fetchPolicy: "network-only" }
  )

  const { data, refetch } = usePaginationFragment<
    AdminMembersReportCustomProfileFieldsTablePaginationQuery,
    AdminMembersReportCustomProfileFieldsTable_PaginationFragment$key
  >(
    graphql`
      fragment AdminMembersReportCustomProfileFieldsTable_PaginationFragment on Organization
      @refetchable(queryName: "AdminMembersReportCustomProfileFieldsTablePaginationQuery")
      @argumentDefinitions(
        first: { type: "Int" }
        after: { type: "String" }
        last: { type: "Int" }
        before: { type: "String" }
        organizationMembershipId: { type: "ID!" }
        isPrivate: { type: "Boolean" }
      ) {
        id
        profileFields(
          first: $first
          after: $after
          last: $last
          before: $before
          status: visible
          defaultTypesToExclude: [public_groups]
          isDefault: false
          isPrivate: $isPrivate
        ) @connection(key: "AdminMembersReportCustomProfileFieldsTable_profileFields") {
          totalCount
          edges {
            cursor
            node {
              id
              ...AdminMembersReportCustomProfileFieldsTableRowFragment
                @arguments(organizationMembershipId: $organizationMembershipId)
            }
          }
          pageInfo {
            endCursor
            startCursor
            hasNextPage
            hasPreviousPage
          }
        }
      }
    `,
    node
  )

  const profileFields = Relay.connectionToArray(data?.profileFields)
  const cursorsList = data?.profileFields.edges.map((e) => e.cursor)

  if (!data) return null

  return (
    <DiscoTable
      headerButtons={
        <DiscoText variant={"heading-xs-600"} marginBottom={1} marginLeft={1}>
          {"Custom Fields"}
        </DiscoText>
      }
      activePage={activePage}
      setActivePage={setActivePage}
      rowsPerPage={PROFILE_FIELDS_PER_PAGE}
      testid={"AdminMembersReportCustomProfileFieldsTable"}
      header={headers}
      rows={profileFields.map((profileField) => (
        <AdminMembersReportCustomProfileFieldsTableRow
          key={profileField.id}
          profileFieldKey={profileField}
        />
      ))}
      connection={{
        cursorsList,
        totalCount: data.profileFields.totalCount,
        pageInfo: {
          endCursor: data.profileFields.pageInfo.endCursor,
          startCursor: data.profileFields.pageInfo.startCursor,
        },
      }}
      onPaginate={refetch}
    />
  )
}

function AdminMembersReportCustomProfileFieldsTableSkeleton() {
  return (
    <DiscoTableSkeletonWithHeader
      header={headers}
      rows={PROFILE_FIELDS_PER_PAGE}
      row={<AdminMembersReportCustomProfileFieldsTableRowSkeleton />}
    />
  )
}

export default Relay.withSkeleton({
  component: AdminMembersReportCustomProfileFieldsTable,
  skeleton: AdminMembersReportCustomProfileFieldsTableSkeleton,
})
